import { memo, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { ChartContext } from '../chart.context'
import { MouseEventParams, Time } from 'lightweight-charts'
import { SeriesGroup } from '../chart.typings'
import ComparePlusIcon from '../../../assets/icons/complarePlus'
import { SpecKeys } from '../../KeyboardShortcuts/Shortcuts.types'
import { useSelector } from 'react-redux'
import { selectIsEmbedded } from '../../../selectors/device'
import { callNative } from '../../../util/nativeBridge'
import { isMacOS } from '../../../util/general'

interface Props {
    setAnchorPositionContextMenu: React.Dispatch<React.SetStateAction<{
        x: number;
        y: number;
        price: string;
        position: 'left' | 'right'
        seriesGroup: SeriesGroup | undefined
    }>>
    paneContainer: HTMLDivElement
    paneIndex: number
    viewKey: string
}

const ChartContextMenuButton = ({ setAnchorPositionContextMenu, paneContainer, paneIndex, viewKey }: Props) => {
    const chartContext = useContext(ChartContext)
    const chartRef = useRef(chartContext!.chart!)
    const btnRef = useRef<HTMLButtonElement>(null)
    const containerRef = useRef<HTMLDivElement>(null)
    const containerRect = useRef<DOMRect>(undefined)
    const currentPaneIndex = useRef<number>(undefined)
    const [plusShortcutEnabled, setPlusShortcutEnabled] = useState(false)
    const isEmbedded = useSelector(selectIsEmbedded)

    const handleKeyDown = useCallback((e: KeyboardEvent) => {
        const detectKey = isMacOS() ? SpecKeys.META : SpecKeys.CONTROL
        if (e.key === detectKey && e.altKey) {
            setPlusShortcutEnabled(true)
        } else {
            setPlusShortcutEnabled(false)
        }
    }, [])

    const handleKeyUp = useCallback((e: KeyboardEvent) => {
        setPlusShortcutEnabled(false)
    }, [])

    useEffect(() => {
        window.addEventListener('keydown', handleKeyDown)
        window.addEventListener('keyup', handleKeyUp)
        return () => {
            window.removeEventListener('keydown', handleKeyDown)
            window.removeEventListener('keyup', handleKeyUp)
        }
    }, [handleKeyDown, handleKeyUp])

    const crossHairSubscription = useCallback((a: MouseEventParams) => {
        if (a.paneIndex !== undefined) currentPaneIndex.current = a.paneIndex
        if (!a.point) {
            if (btnRef.current) btnRef.current.style.opacity = '0'
            return
        }
        if (btnRef.current) {
            if (currentPaneIndex.current === paneIndex && chartContext?.hoverActive) {
                try {
                    btnRef.current.style.opacity = '1'
                    if (a.point?.y) {
                        btnRef.current.style.top = ((a.point.y) - 10.5) + 'px'
                    }
                    if (plusShortcutEnabled) {
                        if (a.point?.x) {
                            const timeScale = chartRef.current.chartApi?.timeScale()
                            if (timeScale) {
                                const nearestTime = timeScale.coordinateToTime(a.point.x)
                                if (nearestTime) {
                                    const nearestXposition = timeScale.timeToCoordinate(nearestTime)
                                    btnRef.current.style.left = ((nearestXposition ?? 0) - 10.5) + 'px'
                                }
                            }
                        }
                    } else {
                        btnRef.current.style.left = ((chartRef.current.chartApi?.timeScale().width() ?? 0) - 22) + 'px'
                    }
                } catch {
                    // coordinateToTime/timeToCoordinate can throw "Value is null" when coords are out of bounds or chart has no data
                    btnRef.current.style.opacity = '0'
                }
            } else {
                btnRef.current.style.opacity = '0'
            }
        }
    }, [paneIndex, chartContext, plusShortcutEnabled])

    useEffect(() => {
        const chart = chartRef.current
        if (chart.chartApi) {
            chart.chartApi.subscribeCrosshairMove(crossHairSubscription)
        }
        return () => {
            if (chart.chartApi) {
                chart.chartApi.unsubscribeCrosshairMove(crossHairSubscription)
            }
        }
    }, [crossHairSubscription])

    const handlePlusClick = useCallback((e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        const y = Number(btnRef.current?.style.top.slice(0, -2)) + 10.5
        const ctx = chartRef.current.getContextMenuSeriesContext(paneIndex)
        let price = ''
        if (ctx) {
            price = ctx.series.coordinateToPrice(y)?.toFixed(2) ?? ''
        }
        if (isEmbedded) {
            callNative('onChartContextMenuClicked', price)
        } else {
            setAnchorPositionContextMenu({
                x: e.clientX,
                y: e.clientY,
                price,
                position: 'right',
                seriesGroup: ctx?.seriesGroupConfig
            })
        }
    }, [setAnchorPositionContextMenu, isEmbedded, paneIndex])

    const handleBtnMouseMove = useCallback((e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        const newYposition = e.clientY - (containerRect.current?.y ?? 0) - 10.5
        const newXposition = e.clientX - (containerRect.current?.x ?? 0)
        const paneSeries = chartRef.current.getContextMenuSeriesContext(paneIndex)?.series
        if (!chartContext?.hoverActive) return
        if (!chartRef.current?.chartApi || !paneSeries) return
        try {
            const timeScale = chartRef.current.chartApi.timeScale()
            const time = plusShortcutEnabled ? timeScale.coordinateToTime(newXposition) : timeScale.coordinateToTime((chartRef.current.chartApi?.timeScale().width() ?? 0) - 10) ?? timeScale.coordinateToTime(0)
            const price = paneSeries.coordinateToPrice(newYposition + 10.5)
            if (price == null || time == null) return
            chartRef.current.chartApi.setCrosshairPosition(price, time, paneSeries)
            if (btnRef.current) {
                btnRef.current.style.opacity = '1'
                btnRef.current.style.top = newYposition + 'px'
                if (time && plusShortcutEnabled) {
                    const nearestXposition = timeScale.timeToCoordinate(time)
                    btnRef.current.style.left = ((nearestXposition ?? 0) - 10.5) + 'px'
                }
            }
        } catch {
            // coordinateToPrice / setCrosshairPosition can throw "Value is null" when coords are out of bounds or chart is updating
        }
    }, [chartContext, plusShortcutEnabled, paneIndex])

    const handleMouseLeave = useCallback(() => {
        if (btnRef.current) {
            btnRef.current.style.opacity = '0'
        }
        if (chartRef.current?.chartApi) {
            try {
                chartRef.current.chartApi.clearCrosshairPosition()
            } catch {
                // clearCrosshairPosition can throw when chart is updating
            }
        }
    }, [])

    useEffect(() => {
        if (containerRef.current) {
            containerRect.current = containerRef.current.getBoundingClientRect()
        }
        const chart = chartRef.current
        const paneElements = chart.chartApi?.getPaneElements?.() ?? []
        const paneEl = paneElements[paneIndex]
        const canvas = paneEl?.querySelector('canvas')
        if (canvas) {
            canvas.addEventListener('mouseleave', handleMouseLeave)
            return () => canvas.removeEventListener('mouseleave', handleMouseLeave)
        }
    }, [paneIndex, handleMouseLeave])

    const containerBox = useMemo(() => {
        return (paneContainer && chartContext && chartRef.current &&
            <div
                id={`chart-${chartContext?.viewKey}-pane-${paneIndex}-context-menu-container`}
                style={{
                    position: 'absolute',
                    width: '100%',
                    height: '100%',
                    top: 0,
                    left: 0,
                    overflow: 'hidden'
                }}
                ref={containerRef}
                onMouseLeave={handleMouseLeave}
            >
                <button
                    ref={btnRef}
                    style={{
                        backgroundColor: '#141722',
                        color: 'white',
                        height: '21.5px',
                        width: '21px',
                        borderTopLeftRadius: '2px',
                        borderBottomLeftRadius: '2px',
                        position: 'absolute',
                        left: (chartRef.current.chartApi?.timeScale().width() ?? 0) - 22,
                        zIndex: 99,
                        top: 0,
                        opacity: 0
                    }}
                    onClick={handlePlusClick}
                    onMouseMove={handleBtnMouseMove}
                >
                    <ComparePlusIcon />
                </button>
            </div>
        )
    }, [handlePlusClick, handleBtnMouseMove, handleMouseLeave, chartContext, paneContainer, paneIndex, chartRef])

    return (
        <>{containerBox}</>
    )
}

export default memo(ChartContextMenuButton)